import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import PatientSectionHeader from '../common/PatientSectionHeader';
import Button from '../../common/Button';
import ToastContainer from '../../common/ToastContainer';
import { useToast } from '../../../hooks/useToast';
import PrescriptionService from '../../../services/prescriptionService';
import PatientService from '../../../services/patientService';
import OrganizationService from '../../../services/organizationService';
import ProfileService from '../../../services/profileService';
import { formatDateEs, printPrescription, downloadPrescriptionPDF, getPrescriptionStatusLabel } from '../../../utils';
import './ViewPrescription.css';

const ViewPrescription = () => {
  const { patientId, prescriptionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const { toasts, removeToast, success: showSuccess, warning: showWarning } = useToast();
  const [patient, setPatient] = useState(null);
  const [prescription, setPrescription] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [doctor, setDoctor] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // Rendering the PDF takes a moment, so the button reports progress.
  const [isDownloading, setIsDownloading] = useState(false);

  // Surface toast messages delivered via navigation state (e.g. after
  // creating this prescription). The sessionStorage flag prevents the
  // toast from being re-shown if React re-runs the effect in StrictMode.
  useEffect(() => {
    const navigationMessage = location.state?.message;
    const navigationMessageId = location.state?.messageId;

    if (!navigationMessage || !navigationMessageId) {
      return;
    }

    const storageKey = `viewPrescriptionToast:${navigationMessageId}`;
    if (!sessionStorage.getItem(storageKey)) {
      const messageType = location.state?.messageType;
      // Warnings get a longer duration so the user can read them before
      // they auto-dismiss; success messages use the default 3s.
      if (messageType === 'warning') {
        showWarning(navigationMessage, 6000);
      } else {
        showSuccess(navigationMessage);
      }
      sessionStorage.setItem(storageKey, 'shown');
    }

    navigate(location.pathname + location.search, { replace: true, state: null });
  }, [location.pathname, location.search, location.state?.message, location.state?.messageId, location.state?.messageType, navigate, showSuccess, showWarning]);

  const loadPatient = useCallback(async () => {
    try {
      const token = auth.user?.access_token;
      const patientData = await PatientService.getPatientById(patientId, token);
      setPatient(patientData);
    } catch (error) {
      console.error('Error loading patient:', error);
      setError('Error al cargar información del paciente');
    }
  }, [patientId, auth.user?.access_token]);

  const loadPrescription = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = auth.user?.access_token;
      const response = await PrescriptionService.getPrescriptionById(prescriptionId, token);
      setPrescription(response.data);

      // Load doctor information if available
      if (response.data?.doctorId) {
        try {
          const doctorData = await ProfileService.getUserProfile(response.data.doctorId, token);
          setDoctor(doctorData);
        } catch (error) {
          console.error('Error loading doctor info:', error);
        }
      }
    } catch (error) {
      console.error('Error loading prescription:', error);
      setError('Error al cargar la prescripción');
    } finally {
      setIsLoading(false);
    }
  }, [prescriptionId, auth.user?.access_token]);

  const loadOrganization = useCallback(async () => {
    try {
      const token = auth.user?.access_token;
      const userId = auth.user?.profile?.sub;
      const organizationData = await OrganizationService.getCurrentUserOrganization(userId, token);
      setOrganization(organizationData);
      return organizationData;
    } catch (error) {
      console.error('Error loading organization:', error);
      return null;
    }
  }, [auth.user?.access_token, auth.user?.profile?.sub]);

  useEffect(() => {
    loadPatient();
    loadPrescription();
    loadOrganization();
  }, [loadPatient, loadPrescription, loadOrganization]);

  const handleEdit = () => {
    navigate(`/patients/${patientId}/prescriptions/${prescriptionId}/edit`);
  };

  const handleCancel = async () => {
    const reason = prompt('Por favor, ingrese la razón de la cancelación:');
    
    if (!reason || reason.trim().length === 0) {
      return; // User cancelled or provided empty reason
    }

    if (!window.confirm(`¿Está seguro que desea cancelar esta prescripción?\n\nRazón: ${reason}`)) {
      return;
    }

    try {
      const token = auth.user?.access_token;
      await PrescriptionService.cancelPrescription(prescriptionId, reason, token);
      
      // Update local state
      setPrescription(prev => ({ ...prev, status: 'cancelled', cancelledReason: reason }));
    } catch (error) {
      console.error('Error cancelling prescription:', error);
      alert('Error al cancelar la prescripción: ' + (error.message || 'Error desconocido'));
    }
  };

  /**
   * Seals the prescription if needed, so printing and downloading go through
   * exactly the same gate. Returns null when the user backs out or the sealing
   * fails, meaning the caller must not emit anything.
   */
  const preparePrescriptionForOutput = async (actionLabel) => {
    // If prescription is draft (not yet printed/sealed), confirm with user and seal it first
    if (prescription.status !== 'draft' && prescription.folioNumber) {
      return prescription;
    }

    const confirmed = window.confirm(
      `¿Está seguro que desea ${actionLabel} esta prescripción?\n\n` +
      `Al ${actionLabel}la se sellará con un número de folio y no podrá editarse después.`
    );

    if (!confirmed) {
      return null;
    }

    // Seal the prescription first so the folio appears in the document
    try {
      const token = auth.user?.access_token;
      const result = await PrescriptionService.markAsPrinted(prescription.id, token);

      // Update local state with the sealed prescription data
      setPrescription(result.data);
      return result.data;
    } catch (error) {
      console.error('Error sealing prescription:', error);
      alert('Error al sellar la prescripción: ' + (error.message || 'Error desconocido'));
      return null;
    }
  };

  const handlePrint = async () => {
    if (!prescription || !patient) return;

    try {
      const prescriptionToPrint = await preparePrescriptionForOutput('imprimir');
      if (!prescriptionToPrint) return;

      // The organization loads in parallel with the prescription; if the user
      // prints before it arrives, fetch it now so the logo is not dropped.
      const org = organization || await loadOrganization();
      printPrescription(prescriptionToPrint, patient, org, doctor, org?.logoViewUrl);
    } catch (error) {
      console.error('Error printing prescription:', error);
      alert('Error al imprimir la prescripción');
    }
  };

  const handleDownload = async () => {
    if (!prescription || !patient) return;

    try {
      const prescriptionToDownload = await preparePrescriptionForOutput('descargar');
      if (!prescriptionToDownload) return;

      setIsDownloading(true);
      // Same race as printing: make sure the organization (and its logo) is
      // loaded before generating the PDF.
      const org = organization || await loadOrganization();
      await downloadPrescriptionPDF(prescriptionToDownload, patient, org, doctor, org?.logoViewUrl);
    } catch (error) {
      console.error('Error downloading prescription:', error);
      alert('Error al descargar la prescripción');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleBack = () => {
    navigate(`/patients/${patientId}/prescriptions`);
  };

  if (error && !patient) {
    return (
      <div className="view-prescription-container">
        <div className="error-message">
          <i className="fas fa-exclamation-triangle"></i>
          <p>{error}</p>
          <Button variant="outline" onClick={handleBack}>
            <i className="fas fa-arrow-left"></i>
            Volver
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="view-prescription-container">
      <PatientSectionHeader
        patientId={patientId}
        patientName={patient ? `${patient.firstName} ${patient.lastName}` : null}
        sectionTitle="Ver Prescripción"
        actionButton={
          <div className="prescription-actions">
            <Button
              variant="outline"
              icon="fas fa-print"
              onClick={handlePrint}
              disabled={!prescription || prescription?.status === 'cancelled'}
              title={prescription?.status === 'cancelled' ? 'No se puede imprimir una prescripción cancelada' : ''}
            >
              <span>Imprimir</span>
            </Button>
            <Button
              variant="outline"
              icon={isDownloading ? 'fas fa-spinner fa-spin' : 'fas fa-download'}
              onClick={handleDownload}
              disabled={!prescription || prescription?.status === 'cancelled' || isDownloading}
              title={prescription?.status === 'cancelled' ? 'No se puede descargar una prescripción cancelada' : ''}
            >
              <span>{isDownloading ? 'Generando...' : 'Descargar'}</span>
            </Button>
            <Button
              variant="primary"
              icon="fas fa-edit"
              onClick={handleEdit}
              disabled={!prescription || prescription?.hasBeenPrinted || prescription?.status === 'cancelled'}
              title={prescription?.hasBeenPrinted ? 'Esta prescripción ya fue emitida y no puede editarse en el sistema' : prescription?.status === 'cancelled' ? 'No se puede editar una prescripción cancelada' : ''}
            >
              <span>Editar</span>
            </Button>
            {prescription?.status !== 'cancelled' && (
              <Button
                variant="outline"
                icon="fas fa-ban"
                onClick={handleCancel}
                disabled={!prescription}
              >
                <span>Cancelar</span>
              </Button>
            )}
          </div>
        }
        onBackClick={handleBack}
      />

      {isLoading ? (
        <div className="prescription-loading">
          <div className="loading-spinner"></div>
        </div>
      ) : error ? (
        <div className="error-message">
          <i className="fas fa-exclamation-triangle"></i>
          <p>{error}</p>
        </div>
      ) : prescription ? (
        <div className="prescription-details">
          <div className="prescription-card">
            <div className="prescription-header">
              <div className="prescription-basic-info">
                <h2>{formatDateEs(prescription.date)}</h2>
                <div className="doctor-info">
                  <i className="fas fa-user-md"></i>
                  <span>Dr. {doctor?.name || prescription.doctorName}</span>
                  {doctor?.licenseId && (
                    <span className="license-info"> - Cédula: {doctor.licenseId}</span>
                  )}
                </div>
                <div className="prescription-id">
                  <i className="fas fa-hashtag"></i>
                  <span>Folio: {prescription.folioNumber || 'Sin asignar (borrador)'}</span>
                </div>
                {prescription.hasBeenPrinted && prescription.status !== 'draft' && (
                  <div className="prescription-badge printed-badge">
                    <i className="fas fa-file-signature"></i>
                    <span>Emitida</span>
                  </div>
                )}
              </div>
              <div className="prescription-status">
                <span className={`status-badge status-${prescription.status}`}>
                  {getPrescriptionStatusLabel(prescription.status)}
                </span>
              </div>
            </div>

            {prescription.status === 'cancelled' && prescription.cancelledReason && (
              <div className="cancellation-notice">
                <i className="fas fa-exclamation-triangle"></i>
                <div>
                  <strong>Prescripción Cancelada</strong>
                  <p>Razón: {prescription.cancelledReason}</p>
                </div>
              </div>
            )}

            <div className="prescription-section">
              <h3>
                <i className="fas fa-pills"></i>
                Medicamentos Prescritos ({prescription.medications?.length || 0})
              </h3>
              {prescription.medications && prescription.medications.length > 0 ? (
                <div className="medications-list">
                  {prescription.medications.map((medication, index) => (
                    <div key={medication.id || index} className="medication-item">
                      <div className="medication-content">
                        <div className="medication-header-simple">
                          <span className="medication-index">{index + 1}.</span>
                          <h4 className="medication-name">
                            {medication.farmaco?.commercialName || medication.farmaco?.components}
                            {medication.farmaco?.commercialName && medication.farmaco?.components && ` (${medication.farmaco?.components})`}
                            {medication.farmaco?.administrationForm && ` - ${medication.farmaco.administrationForm}`}
                          </h4>
                        </div>

                        <div className="medication-details">
                          {medication.farmaco?.dosis && (
                            <div className="medication-dose-info">
                              <span className="dose-label">Dosis:</span>
                              <span className="dose-value">{medication.farmaco.dosis}</span>
                            </div>
                          )}
                          <div className="medication-schedule">
                            <span className="schedule-text">{medication.periodicity} por {medication.duration}</span>
                          </div>
                        </div>

                        {medication.notes && (
                          <div className="medication-notes-simple">
                            <span className="notes-label">Instrucciones:</span>
                            <p>{medication.notes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-medications">
                  <i className="fas fa-pills"></i>
                  <p>No se han prescrito medicamentos</p>
                </div>
              )}
            </div>

            {prescription.generalNotes && (
              <div className="prescription-section">
                <h3>
                  <i className="fas fa-sticky-note"></i>
                  Notas Generales
                </h3>
                <div className="general-notes-content">
                  <p>{prescription.generalNotes}</p>
                </div>
              </div>
            )}

            {prescription.nextCheckupDate && (
              <div className="prescription-section">
                <h3>
                  <i className="fas fa-calendar-check"></i>
                  Próxima Cita de Control
                </h3>
                <div className="checkup-info">
                  <div className="checkup-date">
                    <i className="fas fa-calendar-alt"></i>
                    <span>{formatDateEs(prescription.nextCheckupDate)}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="prescription-metadata">
              <div className="metadata-item">
                <i className="fas fa-clock"></i>
                <span>Creada: {formatDateEs(prescription.createdAt)}</span>
              </div>
              {prescription.updatedAt !== prescription.createdAt && (
                <div className="metadata-item">
                  <i className="fas fa-edit"></i>
                  <span>Última modificación: {formatDateEs(prescription.updatedAt)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="prescription-not-found">
          <i className="fas fa-search"></i>
          <h3>Prescripción no encontrada</h3>
          <p>La prescripción solicitada no existe o no tienes permisos para verla.</p>
        </div>
      )}

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default ViewPrescription;
